<script lang="ts" module>
	export type DagNode = { id: string; x: number; y: number; label: string };
	export type DagEdge = [string, string];

	export const defaultNodes: DagNode[] = [
		{ id: 'trigger', x: 20, y: 48, label: 'trigger' },
		{ id: 'fetch_data', x: 200, y: 48, label: 'fetch_data' },
		{ id: 'classify', x: 380, y: 48, label: 'classify' },
		{ id: 'call_agent', x: 200, y: 148, label: 'call_agent' },
		{ id: 'route', x: 380, y: 148, label: 'route_to_team' },
		{ id: 'notify', x: 560, y: 98, label: 'notify' }
	];

	export const defaultEdges: DagEdge[] = [
		['trigger', 'fetch_data'],
		['fetch_data', 'classify'],
		['fetch_data', 'call_agent'],
		['call_agent', 'route'],
		['classify', 'notify'],
		['route', 'notify']
	];
</script>

<script lang="ts">
	import { onDestroy, onMount } from 'svelte';

	interface Props {
		bare?: boolean;
		nodes?: DagNode[];
		edges?: DagEdge[];
		/**
		 * When false, the sequential step animation is paused — the DAG
		 * freezes with node 0 in the "running" state and the rest queued.
		 */
		animated?: boolean;
	}

	const {
		bare = false,
		nodes = defaultNodes,
		edges = defaultEdges,
		animated = true
	}: Props = $props();

	const NODE_W = 150;
	const NODE_H = 32;

	let step = $state(0);
	let timer: ReturnType<typeof setInterval> | undefined;

	onMount(() => {
		if (!animated) return;
		timer = setInterval(() => {
			step = (step + 1) % (nodes.length + 2);
		}, 900);
	});

	onDestroy(() => {
		if (timer) clearInterval(timer);
	});

	function stateOf(i: number): 'done' | 'running' | 'queued' {
		if (step > i) return 'done';
		if (step === i) return 'running';
		return 'queued';
	}

	function nodeStroke(st: 'done' | 'running' | 'queued'): string {
		if (st === 'done') return 'oklch(0.7181 0.1899 146.65)';
		if (st === 'running') return 'oklch(0.7 0.18 220)';
		return 'var(--border-subtle)';
	}

	function edgeState(fromIdx: number, toIdx: number): 'done' | 'active' | 'queued' {
		if (step > toIdx) return 'done';
		if (step >= fromIdx && step <= toIdx) return 'active';
		return 'queued';
	}

	function edgeStroke(st: 'done' | 'active' | 'queued'): string {
		if (st === 'done') return 'oklch(0.5 0.15 145)';
		if (st === 'active') return 'oklch(0.6 0.2 220)';
		return 'var(--border-subtle)';
	}

	function edgePath(a: (typeof nodes)[number], b: (typeof nodes)[number]): string {
		const ax = a.x + NODE_W;
		const ay = a.y + NODE_H / 2;
		const bx = b.x;
		const by = b.y + NODE_H / 2;
		const mid = (ax + bx) / 2;
		return `M ${ax} ${ay} C ${mid} ${ay}, ${mid} ${by}, ${bx} ${by}`;
	}

	function nodeById(id: string) {
		return nodes.find((n) => n.id === id)!;
	}
</script>

<div class="dag" class:bare>
	<div class="dag-grid"></div>
	<svg viewBox="0 0 760 220" preserveAspectRatio="xMidYMid meet" class="relative block h-full w-full">
		{#each edges as [from, to], i (i)}
			{@const a = nodeById(from)}
			{@const b = nodeById(to)}
			{@const fi = nodes.findIndex((n) => n.id === from)}
			{@const ti = nodes.findIndex((n) => n.id === to)}
			{@const st = edgeState(fi, ti)}
			<path
				d={edgePath(a, b)}
				stroke={edgeStroke(st)}
				stroke-width="1.5"
				fill="none"
				stroke-dasharray={st === 'active' ? '4 4' : '0'}
			>
				{#if st === 'active'}
					<animate
						attributeName="stroke-dashoffset"
						from="0"
						to="-8"
						dur="0.6s"
						repeatCount="indefinite"
					/>
				{/if}
			</path>
		{/each}

		{#each nodes as n, i (n.id)}
			{@const st = stateOf(i)}
			{@const stroke = nodeStroke(st)}
			<g transform="translate({n.x} {n.y})">
				<rect
					width={NODE_W}
					height={NODE_H}
					rx="6"
					fill="var(--surface-raised)"
					{stroke}
					stroke-width="1"
				/>
				<circle cx="12" cy={NODE_H / 2} r="3.5" fill={stroke}>
					{#if st === 'running'}
						<animate attributeName="opacity" values="1;0.3;1" dur="1.2s" repeatCount="indefinite" />
					{/if}
				</circle>
				<text
					x="24"
					y={NODE_H / 2 + 4}
					font-family="var(--font-mono)"
					font-size="11"
					fill="var(--foreground)"
				>
					{n.label}
				</text>
				{#if st === 'done'}
					<g transform="translate({NODE_W - 22} {NODE_H / 2 - 6})">
						<path
							d="M2 6 5 9 12 2"
							stroke="oklch(0.75 0.18 145)"
							stroke-width="1.8"
							fill="none"
							stroke-linecap="round"
							stroke-linejoin="round"
						/>
					</g>
				{:else if st === 'running'}
					<g transform="translate({NODE_W - 18} {NODE_H / 2 - 6})">
						<circle
							cx="5"
							cy="6"
							r="5"
							fill="none"
							stroke="oklch(0.7 0.18 220)"
							stroke-width="1.5"
							stroke-dasharray="10 18"
							stroke-linecap="round"
						>
							<animateTransform
								attributeName="transform"
								type="rotate"
								from="0 5 6"
								to="360 5 6"
								dur="1s"
								repeatCount="indefinite"
							/>
						</circle>
					</g>
				{/if}
			</g>
		{/each}
	</svg>
</div>

<style>
	.dag {
		position: relative;
		border: 1px solid var(--border);
		border-radius: var(--radius);
		background: var(--surface-sunken);
		padding: 28px;
		overflow: hidden;
		aspect-ratio: 760 / 300;
		width: 100%;
		box-shadow: 0 20px 60px -30px rgba(0, 0, 0, 0.6);
	}
	.dag.bare {
		padding: 20px;
		box-shadow: none;
		aspect-ratio: 760 / 260;
	}
	.dag-grid {
		position: absolute;
		inset: 0;
		background-image: radial-gradient(rgba(255, 255, 255, 0.09) 1px, transparent 0);
		background-size: 20px 20px;
		opacity: 0.6;
		pointer-events: none;
	}
</style>
